// Device images come from two places. The mobile app uploads a photo of the
// physical sensor to S3, which is the shared source of truth and is what
// `fetchDevicePhotoUrl` returns. Older images added from this dashboard were
// only ever kept in the browser as data URLs, so the localStorage map below is
// retained as a fallback for anything captured before the app existed.

import axiosInstance from '../_helpers/interceptor';
import { DEV_NEW_DOMAIN_URL } from '../environment';

const DEVICE_IMAGES_KEY = 'telosair_device_images';

type DeviceImageMap = Record<string, string>;

/**
 * Reads the full device-image map from localStorage.
 * @returns - Map of serial number to image data URL.
 */
function readAll(): DeviceImageMap {
  try {
    const raw = localStorage.getItem(DEVICE_IMAGES_KEY);
    return raw ? (JSON.parse(raw) as DeviceImageMap) : {};
  } catch {
    return {};
  }
}

/**
 * Gets the stored image for a device.
 * @param serialNumber - Device serial number.
 * @returns - Image data URL, or null when none is stored.
 */
export function getDeviceImage(serialNumber: number | string | null | undefined): string | null {
  if (serialNumber === null || serialNumber === undefined) {
    return null;
  }
  return readAll()[String(serialNumber)] || null;
}

/**
 * Stores (or replaces) the image for a device.
 * @param serialNumber - Device serial number.
 * @param dataUrl - Image data URL.
 * @returns - True when the image was persisted, false when storage failed.
 */
export function storeDeviceImage(serialNumber: number | string, dataUrl: string): boolean {
  try {
    const all = readAll();
    all[String(serialNumber)] = dataUrl;
    localStorage.setItem(DEVICE_IMAGES_KEY, JSON.stringify(all));
    return true;
  } catch {
    return false;
  }
}

/**
 * Removes the stored image for a device.
 * @param serialNumber - Device serial number.
 */
export function removeDeviceImage(serialNumber: number | string): void {
  try {
    const all = readAll();
    delete all[String(serialNumber)];
    localStorage.setItem(DEVICE_IMAGES_KEY, JSON.stringify(all));
  } catch {
    // ignore – nothing we can do if storage is unavailable
  }
}

type DevicePhotoResponse = {
  serialNumber: number;
  photoKey: string | null;
  photoUrl: string | null;
};

// Presigned URLs are valid for an hour, but hover cards mount and unmount
// constantly — without a cache every pass over the map would refetch. Entries
// are held well inside the signature's lifetime and dropped on write.
const PHOTO_CACHE_TTL_MS = 5 * 60 * 1000;
const photoCache = new Map<string, { url: string | null; at: number }>();

/**
 * Drops a device's cached photo URL so the next read re-fetches.
 * @param serialNumber - Device serial number.
 */
export function invalidateDevicePhoto(serialNumber: number | string): void {
  photoCache.delete(String(serialNumber));
}

/**
 * Fetches the sensor photo uploaded from the mobile app or the device form.
 *
 * Resolves to null when the device has no photo, is not visible to this
 * client, or the request fails — callers simply render no image.
 * @param serialNumber - Device serial number.
 * @param signal - Optional abort signal.
 * @returns - A displayable image URL, or null.
 */
export async function fetchDevicePhotoUrl(
  serialNumber: number | string | null | undefined,
  signal?: AbortSignal
): Promise<string | null> {
  if (serialNumber === null || serialNumber === undefined) {
    return null;
  }
  const key = String(serialNumber);
  const cached = photoCache.get(key);
  if (cached && Date.now() - cached.at < PHOTO_CACHE_TTL_MS) {
    return cached.url;
  }
  try {
    const res = await axiosInstance.get<DevicePhotoResponse>(
      `${DEV_NEW_DOMAIN_URL}/device/${key}/photo`,
      { signal }
    );
    const url = res.data?.photoUrl || null;
    photoCache.set(key, { url, at: Date.now() });
    return url;
  } catch {
    // Don't cache failures — an aborted or offline request should retry.
    return null;
  }
}

/**
 * Resolves the best available image for a device: the shared photo stored on
 * the server, falling back to a legacy browser-local image.
 * @param serialNumber - Device serial number.
 * @param signal - Optional abort signal.
 * @returns - A displayable image URL, or null when there is none.
 */
export async function resolveDeviceImage(
  serialNumber: number | string | null | undefined,
  signal?: AbortSignal
): Promise<string | null> {
  const uploaded = await fetchDevicePhotoUrl(serialNumber, signal);
  return uploaded || getDeviceImage(serialNumber);
}

/**
 * Uploads a device photo to the server so every user of the client can see it.
 * @param serialNumber - Device serial number.
 * @param blob - The (already downscaled) image data.
 * @returns - The signed URL of the stored photo.
 */
export async function uploadDevicePhoto(
  serialNumber: number | string,
  blob: Blob
): Promise<string | null> {
  const form = new FormData();
  form.append('file', blob, `device-${serialNumber}.jpg`);
  const res = await axiosInstance.post<DevicePhotoResponse>(
    `${DEV_NEW_DOMAIN_URL}/device/${serialNumber}/photo`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
  invalidateDevicePhoto(serialNumber);
  return res.data?.photoUrl || null;
}

/**
 * Removes a device's photo from the server.
 * @param serialNumber - Device serial number.
 */
export async function deleteDevicePhoto(serialNumber: number | string): Promise<void> {
  await axiosInstance.delete(`${DEV_NEW_DOMAIN_URL}/device/${serialNumber}/photo`);
  invalidateDevicePhoto(serialNumber);
}
